import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Link, Typography, useTheme } from '@mui/material';

import { useWhatsAppClick } from '@/hooks/useWhatsAppClick';
import { captureException } from '@/services/sentryService';
import { isChunkLoadError } from '@/utils/chunkLoadError';
import { PLAUSIBLE_GOALS, trackPlausibleEvent } from '@/utils/plausible';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  resetOnRetry?: boolean;
  sourceLocation?: string;
  retryable?: boolean;
  maxRetries?: number;
  fallbackRoute?: string;
  onError?: (
    error: Error,
    errorInfo: React.ErrorInfo,
    context: { sourceLocation: string }
  ) => void;
  onNavigate?: (route: string) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  retryAttempt: number;
  countdown: number;
  canRetry: boolean;
  isChunkLoadError?: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  static defaultProps = {
    resetOnRetry: false,
    sourceLocation: 'error_boundary',
    retryable: true,
    maxRetries: 3,
  };

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryAttempt: 0,
      countdown: 0,
      canRetry: true,
    };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    const isChunkError = isChunkLoadError(error);

    const maxRetries = this.props.maxRetries || 3;
    const currentAttempt = this.state.retryAttempt;
    const hasReachedMaxRetries = currentAttempt >= maxRetries;
    const isRetryable = this.props.retryable !== false;
    const canRetry = isRetryable && !hasReachedMaxRetries;

    this.setState({
      error,
      errorInfo,
      countdown: 0,
      canRetry,
      isChunkLoadError: isChunkError,
    });

    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo, {
          sourceLocation: this.props.sourceLocation || 'error_boundary',
        });
      } catch (callbackError) {
        console.warn('Error in onError callback:', callbackError);
      }
    }

    try {
      captureException(error, {
        tags: {
          sourceLocation: this.props.sourceLocation || 'error_boundary',
        },
        extra: {
          componentStack: errorInfo.componentStack,
          errorBoundary: true,
        },
      });
    } catch (sentryError) {
      console.warn('Failed to report error to Sentry:', sentryError);
    }

    if (isChunkError) {
      try {
        trackPlausibleEvent(PLAUSIBLE_GOALS.CHUNK_LOAD_ERROR, {
          route: typeof window !== 'undefined' ? window.location.pathname : '',
        });
      } catch (analyticsError) {
        console.warn(
          'Failed to track chunk-load error in Plausible:',
          analyticsError
        );
      }
    }

    if (isRetryable && !hasReachedMaxRetries && !isChunkError) {
      this.startCountdown();
    }
  }

  componentWillUnmount(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  startCountdown = (): void => {
    const maxRetries = this.props.maxRetries || 3;
    const currentAttempt = this.state.retryAttempt;

    if (currentAttempt >= maxRetries) {
      this.setState({ canRetry: false });
      return;
    }

    const backoffDelay = Math.pow(2, currentAttempt) * 1000;
    const initialCountdown = Math.floor(backoffDelay / 1000);

    this.setState({ countdown: initialCountdown });

    this.countdownTimer = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.countdown - 1;
        if (newCountdown <= 0) {
          clearInterval(this.countdownTimer!);
          this.countdownTimer = null;
          return { countdown: 0 };
        }
        return { countdown: newCountdown };
      });
    }, 1000);
  };

  handleReset = (): void => {
    const maxRetries = this.props.maxRetries || 3;
    const nextAttempt = this.state.retryAttempt + 1;

    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }

    if (this.state.isChunkLoadError) {
      window.location.reload();
      return;
    }

    if (nextAttempt >= maxRetries) {
      this.setState({
        retryAttempt: nextAttempt,
        canRetry: false,
        countdown: 0,
      });
      return;
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryAttempt: nextAttempt,
      countdown: 0,
    });

    if (this.props.resetOnRetry) {
      window.location.reload();
    }
  };

  handleNavigateToFallback = (): void => {
    if (this.props.fallbackRoute && this.props.onNavigate) {
      this.props.onNavigate(this.props.fallbackRoute);
    }
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <SafeErrorFallbackBoundary
          sourceLocation={this.props.sourceLocation || 'error_boundary'}
        >
          <ErrorFallback
            onReset={this.handleReset}
            resetOnRetry={
              this.props.resetOnRetry || this.state.isChunkLoadError
            }
            sourceLocation={this.props.sourceLocation || 'error_boundary'}
            retryable={this.props.retryable !== false}
            canRetry={this.state.canRetry}
            countdown={this.state.countdown}
            retryAttempt={this.state.retryAttempt}
            maxRetries={this.props.maxRetries || 3}
            fallbackRoute={this.props.fallbackRoute}
            onNavigate={this.props.onNavigate}
            onNavigateToFallback={this.handleNavigateToFallback}
            isChunkLoadError={this.state.isChunkLoadError}
          />
        </SafeErrorFallbackBoundary>
      );
    }

    return this.props.children;
  }
}

function UltimateFallback(): React.ReactElement {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: '32px 24px',
        fontFamily: "'Sora', 'Sora Fallback', system-ui, sans-serif",
        background:
          'linear-gradient(160deg, #F5F7FC 0%, #EDF0F9 50%, #E2E6F2 100%)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '440px',
          textAlign: 'center',
          background: '#fff',
          borderRadius: '20px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          padding: '40px 32px',
        }}
      >
        <div
          style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </div>
        <h1
          style={{
            margin: '0 0 12px',
            fontSize: '1.375rem',
            fontWeight: 600,
            color: '#1e293b',
            letterSpacing: '-0.02em',
          }}
        >
          Something went wrong
        </h1>
        <p
          style={{
            margin: '0 0 28px',
            fontSize: '0.9375rem',
            lineHeight: 1.5,
            color: '#64748b',
          }}
        >
          We encountered an unexpected error. Please reload the page to
          continue.
        </p>
        <button
          type="button"
          onClick={handleReload}
          style={{
            padding: '12px 24px',
            fontSize: '0.9375rem',
            fontWeight: 600,
            color: '#fff',
            background: 'linear-gradient(135deg, #3A56D4 0%, #2A3EB1 100%)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(58, 86, 212, 0.35)',
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseOver={e => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow =
              '0 4px 12px rgba(58, 86, 212, 0.4)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow =
              '0 2px 8px rgba(58, 86, 212, 0.35)';
          }}
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

interface SafeErrorFallbackBoundaryProps {
  sourceLocation: string;
  children: React.ReactNode;
}

interface SafeErrorFallbackBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class SafeErrorFallbackBoundary extends React.Component<
  SafeErrorFallbackBoundaryProps,
  SafeErrorFallbackBoundaryState
> {
  constructor(props: SafeErrorFallbackBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(
    error: Error
  ): SafeErrorFallbackBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      'SafeErrorFallbackBoundary caught an error from ErrorFallback:',
      error,
      errorInfo
    );

    try {
      captureException(error, {
        tags: {
          sourceLocation: this.props.sourceLocation || 'error_boundary',
          errorFallbackFailure: 'true',
        },
        extra: {
          componentStack: errorInfo?.componentStack,
          errorBoundary: true,
          errorFallbackFailure: true,
        },
      });
    } catch (sentryError) {
      console.warn(
        'Failed to report ErrorFallback error to Sentry:',
        sentryError
      );
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return <UltimateFallback />;
    }

    return this.props.children;
  }
}

interface ErrorFallbackProps {
  onReset: () => void;
  resetOnRetry?: boolean;
  sourceLocation: string;
  retryable: boolean;
  canRetry: boolean;
  countdown: number;
  retryAttempt: number;
  maxRetries: number;
  fallbackRoute?: string;
  onNavigate?: (route: string) => void;
  onNavigateToFallback: () => void;
  isChunkLoadError?: boolean;
}

function ErrorFallback({
  onReset,
  resetOnRetry,
  sourceLocation,
  retryable,
  canRetry,
  countdown,
  retryAttempt,
  maxRetries,
  fallbackRoute,
  onNavigate,
  onNavigateToFallback,
  isChunkLoadError = false,
}: ErrorFallbackProps): React.ReactElement {
  const theme = useTheme();
  const handleWhatsAppClick = useWhatsAppClick(sourceLocation, {
    includeJourney: false,
  });

  const hasReachedMaxRetries = retryAttempt >= maxRetries;
  const showCountdown = countdown > 0 && canRetry && !hasReachedMaxRetries;

  const primaryGradient =
    (theme as unknown as Record<string, Record<string, Record<string, string>>>)
      .custom?.gradients?.primary || 'linear-gradient(90deg, #3A56D4, #2A3EB1)';

  return (
    <Box
      role="alert"
      aria-live="assertive"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        p: 3,
        background:
          'linear-gradient(160deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '480px',
          textAlign: 'center',
          bgcolor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
          p: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            mx: 'auto',
            mb: 2.5,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </Box>

        <Typography
          variant="h5"
          component="h1"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            letterSpacing: '-0.02em',
            mb: 1,
          }}
        >
          Something went wrong
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.55,
            mb: 2,
            maxWidth: '36ch',
            mx: 'auto',
          }}
        >
          {hasReachedMaxRetries
            ? 'We encountered an unexpected error multiple times. Please contact us for assistance.'
            : isChunkLoadError
              ? 'A new version of the app may be available. Reload the page to get the latest version.'
              : 'We encountered an unexpected error. Please try again or contact us if the problem\u00A0persists.'}
        </Typography>

        {(showCountdown || (retryAttempt > 0 && !hasReachedMaxRetries)) && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            {showCountdown && (
              <Box
                component="span"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: 'action.hover',
                  typography: 'body2',
                  color: 'text.secondary',
                  fontWeight: 500,
                }}
              >
                Retry in {countdown}s
              </Box>
            )}
            {retryAttempt > 0 && !hasReachedMaxRetries && (
              <Typography variant="body2" color="text.secondary">
                Attempt {retryAttempt} of {maxRetries}
              </Typography>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          {retryable && canRetry && !hasReachedMaxRetries && (
            <Button
              variant="contained"
              onClick={onReset}
              disabled={countdown > 0}
              sx={{
                background: primaryGradient,
                color: 'white',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(99, 102, 241, 0.35)',
                '&:hover': {
                  background: primaryGradient,
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                  filter: 'brightness(1.05)',
                },
                '&:disabled': {
                  color: 'white',
                  opacity: 0.8,
                },
              }}
            >
              {resetOnRetry || isChunkLoadError
                ? 'Reload page'
                : countdown > 0
                  ? `Wait ${countdown}s`
                  : 'Try again'}
            </Button>
          )}

          {fallbackRoute && onNavigate && (
            <Button
              variant="outlined"
              onClick={onNavigateToFallback}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  bgcolor: 'action.hover',
                },
              }}
            >
              Go to safe page
            </Button>
          )}

          <Link
            component="button"
            type="button"
            onClick={handleWhatsAppClick}
            sx={{
              cursor: 'pointer',
              textDecoration: 'none',
              color: 'primary.main',
              fontWeight: 500,
              fontSize: '0.9375rem',
              py: 1,
              px: 1.5,
              borderRadius: 2,
              '&:hover': {
                textDecoration: 'none',
                bgcolor: 'action.hover',
              },
            }}
          >
            Need help? Contact us on WhatsApp (8012 7012)
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

function ErrorBoundaryWithRouter(
  props: Omit<ErrorBoundaryProps, 'onNavigate'>
): React.ReactElement {
  const navigate = useNavigate();
  useLocation();

  const handleNavigate = (route: string) => {
    navigate(route);
  };

  return <ErrorBoundary {...props} onNavigate={handleNavigate} />;
}

interface RouterErrorBoundaryProps {
  children: React.ReactNode;
  errorBoundaryProps: ErrorBoundaryProps;
}

interface RouterErrorBoundaryState {
  hasError: boolean;
}

class RouterErrorBoundary extends React.Component<
  RouterErrorBoundaryProps,
  RouterErrorBoundaryState
> {
  constructor(props: RouterErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(
    error: Error
  ): RouterErrorBoundaryState | never {
    if (
      error?.message?.includes('useNavigate') ||
      error?.message?.includes('useLocation') ||
      error?.message?.includes('useRoutes') ||
      error?.message?.includes('Router')
    ) {
      return { hasError: true };
    }
    throw error;
  }

  componentDidCatch(error: Error, _errorInfo: React.ErrorInfo): void {
    if (
      error?.message?.includes('useNavigate') ||
      error?.message?.includes('useLocation') ||
      error?.message?.includes('useRoutes') ||
      error?.message?.includes('Router')
    ) {
      // Router context not available - expected when ErrorBoundary is used outside Router
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      const handleNavigate = (route: string) => {
        window.location.href = route;
      };

      return (
        <ErrorBoundary
          {...this.props.errorBoundaryProps}
          onNavigate={handleNavigate}
        />
      );
    }

    return this.props.children;
  }
}

function ErrorBoundaryWithNavigation({
  resetOnRetry = false,
  sourceLocation = 'error_boundary',
  retryable = true,
  maxRetries = 3,
  ...props
}: ErrorBoundaryProps): React.ReactElement {
  return (
    <RouterErrorBoundary
      errorBoundaryProps={{
        resetOnRetry,
        sourceLocation,
        retryable,
        maxRetries,
        ...props,
      }}
    >
      <ErrorBoundaryWithRouter
        resetOnRetry={resetOnRetry}
        sourceLocation={sourceLocation}
        retryable={retryable}
        maxRetries={maxRetries}
        {...props}
      />
    </RouterErrorBoundary>
  );
}

export default ErrorBoundaryWithNavigation;
export { ErrorBoundary };
